import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, Switch, TextField, Typography,
} from '@mui/material';
import { Print as PrintIcon, Save as SaveIcon } from '@mui/icons-material';
import axios from 'axios';
import useSWR from 'swr';
import { apiHost } from '../utils/apiHost';
import {
  BAGIAN_TIKET, JUDUL_TIKET, KOLOM_58MM, KOLOM_80MM, PENUTUP_TIKET, buatBarisTiket, urutanTiketLengkap,
} from '../struk/strukLayout';
import { SKEMA_TIKET, SusunanStruk } from './StrukSusunan';
import StrukPratinjau from '../struk/StrukPratinjau';
import { cetakBrowser } from '../struk/cetakStruk';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const api = axios.create({ baseURL: getApiBaseUrl(), withCredentials: true });
const fetcher = (url) => api.get(url).then((r) => r.data);

const MAKS_JUDUL = 40;
const MAKS_PENUTUP = 120;
const MAKS_BARIS = 3;

const SAKELAR_AWAL = Object.fromEntries(BAGIAN_TIKET.map((k) => [k, true]));

// Contoh pesanan untuk pratinjau (tidak disimpan).
const CONTOH = {
  dapur: {
    waktu: '17/10/2026 14:05',
    orderId: 'ORDER-1760000000000',
    kasir: 'Rina',
    customer: 'Budi',
    meja: 'A3',
    nomorAntrian: '012',
    noted: 'tanpa sambal',
    items: [{ nama: 'Mie SP 2', qty: 2, catatan: 'pedas' }, { nama: 'Nasi Goreng', qty: 1 }],
  },
  bar: {
    waktu: '17/10/2026 14:05',
    orderId: 'ORDER-1760000000000',
    kasir: 'Rina',
    customer: 'Budi',
    meja: 'A3',
    nomorAntrian: '012',
    noted: 'tanpa sambal',
    items: [{ nama: 'Es Teh', qty: 1, catatan: 'kurang manis' }, { nama: 'Kopi Susu', qty: 2 }],
  },
};

const NAMA = { dapur: 'dapur', bar: 'bar' };

/** Pengaturan tiket (dapur atau bar) per cabang: lebar, judul, gaya item, pesan penutup, bagian & urutan, dengan pratinjau. */
export const StrukTiket = ({ jenis, cabangUuid, konfigurasi, namaCabang, mutate }) => {
  const kolomKonfig = jenis === 'bar' ? 'tiketBar' : 'tiketDapur';
  const [lebar, setLebar] = useState(58);
  const [judul, setJudul] = useState('');
  const [gayaItem, setGayaItem] = useState('qty');
  const [penutup, setPenutup] = useState('');
  const [tampil, setTampil] = useState(SAKELAR_AWAL);
  const [urutan, setUrutan] = useState(urutanTiketLengkap(null));
  const [menyimpan, setMenyimpan] = useState(false);
  const [umpanBalik, setUmpanBalik] = useState(null);
  // Pemisahan menu per printer (disimpan bersama tiket bar): kategori yang dicetak di BAR, sisanya di DAPUR.
  const [pisahAktif, setPisahAktif] = useState(false);
  const [kategoriBar, setKategoriBar] = useState([]);
  const { data: daftarKategori } = useSWR(jenis === 'bar' ? '/kategori' : null, fetcher);
  const kategori = daftarKategori?.data || [];

  useEffect(() => {
    const p = (konfigurasi && konfigurasi.tiketPisah) || {};
    setPisahAktif(p.aktif === true);
    setKategoriBar(Array.isArray(p.bar) ? p.bar : []);
  }, [konfigurasi]);

  useEffect(() => {
    const t = (konfigurasi && konfigurasi[kolomKonfig]) || {};
    setLebar(Number(t.lebar) === 80 ? 80 : 58);
    setJudul(t.judul || '');
    setGayaItem(t.gayaItem === 'x' ? 'x' : 'qty');
    setPenutup(t.penutup || '');
    setTampil({ ...SAKELAR_AWAL, ...(t.tampil && typeof t.tampil === 'object' ? t.tampil : {}) });
    setUrutan(urutanTiketLengkap(t.urutan));
  }, [konfigurasi, kolomKonfig]);

  const jumlahBarisPenutup = penutup ? penutup.split('\n').length : 0;
  const penutupTerlaluPanjang = penutup.length > MAKS_PENUTUP || jumlahBarisPenutup > MAKS_BARIS;
  const judulTerlaluPanjang = judul.length > MAKS_JUDUL;

  // Pratinjau: mesin tata letak SAMA dengan yang dipakai pencetak aplikasi HP & halaman Dapur (struk/strukLayout.js).
  const baris = useMemo(() => buatBarisTiket({
    trx: { namaCabang: namaCabang || 'Nama Cabang', ...CONTOH[jenis] },
    jenis,
    kolom: lebar === 80 ? KOLOM_80MM : KOLOM_58MM,
    judul,
    gayaItem,
    penutup,
    tampil,
    urutan,
  }), [namaCabang, jenis, lebar, judul, gayaItem, penutup, tampil, urutan]);

  const simpan = async () => {
    setMenyimpan(true);
    setUmpanBalik(null);
    try {
      await api.put('/updatekonfigurasicabang', {
        cabangUuid,
        [kolomKonfig]: { lebar, judul, gayaItem, penutup, tampil, urutan },
        ...(jenis === 'bar' ? { tiketPisah: { aktif: pisahAktif, bar: kategoriBar } } : {}),
      });
      await mutate();
      setUmpanBalik({ type: 'success', msg: `Pengaturan tiket ${NAMA[jenis]} disimpan. Berlaku di aplikasi HP (saat berikutnya terhubung) dan halaman Dapur.` });
    } catch (err) {
      setUmpanBalik({ type: 'error', msg: err.response?.data?.msg || err.response?.data?.message || 'Gagal menyimpan pengaturan tiket.' });
    } finally {
      setMenyimpan(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start', maxWidth: 1180 }}>
      <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          {jenis === 'bar'
            ? 'Tiket bar dicetak ke printer BAR di aplikasi HP (mode multi-printer), tanpa harga.'
            : 'Tiket dapur dicetak ke printer KITCHEN di aplikasi HP (mode multi-printer) dan lewat tombol cetak di halaman Dapur, tanpa harga.'}
        </Alert>

        {jenis === 'dapur' && konfigurasi?.tiketPisah?.aktif && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Pemisahan menu per printer aktif: kategori yang dipetakan ke BAR (tab Tiket Bar) tidak dicetak di tiket dapur.
            Bila HP tidak memasang printer BAR, item itu dialihkan ke tiket dapur.
          </Alert>
        )}

        {jenis === 'bar' && (
          <Card sx={{ mb: 2 }}>
            <CardHeader
              title="Pisahkan menu per printer"
              subheader="Mati: BAR dan DAPUR sama-sama mencetak semua item. Aktif: kategori yang dicentang dicetak di BAR, sisanya di DAPUR."
              action={<Switch checked={pisahAktif} onChange={(e) => setPisahAktif(e.target.checked)} disabled={menyimpan} />}
            />
            {pisahAktif && (
              <CardContent>
                <FormLabel>Kategori yang dicetak di BAR</FormLabel>
                <FormGroup sx={{ maxHeight: 260, overflowY: 'auto', flexWrap: 'nowrap' }}>
                  {kategori.length === 0 && <Typography variant="body2" color="text.secondary">Belum ada kategori produk.</Typography>}
                  {kategori.map((k) => (
                    <FormControlLabel
                      key={k.uuid}
                      control={(
                        <Checkbox
                          size="small"
                          checked={kategoriBar.includes(k.uuid)}
                          disabled={menyimpan}
                          onChange={(e) => setKategoriBar((p) => (e.target.checked ? [...p, k.uuid] : p.filter((x) => x !== k.uuid)))}
                        />
                      )}
                      label={k.namakategori}
                    />
                  ))}
                </FormGroup>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Kategori yang tidak dicentang (termasuk produk baru tanpa kategori) dicetak di DAPUR. Tiket yang tidak punya item tidak dicetak.
                  Bila HP hanya memasang salah satu printer, semua item dialihkan ke printer itu. Struk kasir tetap memuat semua item.
                </Typography>
              </CardContent>
            )}
          </Card>
        )}

        <Card sx={{ mb: 2 }}>
          <CardHeader title="Kertas" subheader="Lebar printer tiket bisa berbeda dari printer kasir." />
          <CardContent>
            <FormControl>
              <FormLabel>Lebar kertas printer</FormLabel>
              <RadioGroup row value={lebar} onChange={(e) => setLebar(Number(e.target.value))}>
                <FormControlLabel value={58} control={<Radio />} label="58 mm (32 karakter)" />
                <FormControlLabel value={80} control={<Radio />} label="80 mm (48 karakter)" />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardHeader title="Judul & gaya item" />
          <CardContent>
            <TextField
              fullWidth
              size="small"
              label="Judul tiket"
              placeholder={JUDUL_TIKET[jenis]}
              value={judul}
              onChange={(e) => setJudul(e.target.value)}
              disabled={menyimpan}
              error={judulTerlaluPanjang}
              helperText={judulTerlaluPanjang ? `Maksimal ${MAKS_JUDUL} karakter.` : 'Kosongkan untuk memakai judul bawaan (tampil abu-abu).'}
              sx={{ mb: 2 }}
            />
            <FormControl>
              <FormLabel>Gaya daftar item</FormLabel>
              <RadioGroup row value={gayaItem} onChange={(e) => setGayaItem(e.target.value)}>
                <FormControlLabel value="qty" control={<Radio />} label='Nama lalu "Qty: 2"' />
                <FormControlLabel value="x" control={<Radio />} label='"2x Nama" (tebal, mudah dibaca)' />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        <SusunanStruk skema={SKEMA_TIKET} urutan={urutan} setUrutan={setUrutan} tampil={tampil} setTampil={setTampil} disabled={menyimpan} />

        <Card sx={{ mb: 2 }}>
          <CardHeader title="Pesan penutup" subheader={`Bawaan: "${PENUTUP_TIKET}". Kosongkan untuk memakai bawaan.`} />
          <CardContent>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={3}
              size="small"
              label="Pesan penutup"
              placeholder={PENUTUP_TIKET}
              value={penutup}
              onChange={(e) => setPenutup(e.target.value)}
              disabled={menyimpan}
              error={penutupTerlaluPanjang}
              helperText={penutupTerlaluPanjang
                ? `Maksimal ${MAKS_BARIS} baris dan ${MAKS_PENUTUP} karakter.`
                : `${penutup.length}/${MAKS_PENUTUP} karakter, ${jumlahBarisPenutup}/${MAKS_BARIS} baris`}
            />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={simpan} disabled={menyimpan || penutupTerlaluPanjang || judulTerlaluPanjang}>
            {menyimpan ? 'Menyimpan...' : 'Simpan'}
          </Button>
          {umpanBalik && <Alert severity={umpanBalik.type} sx={{ flex: 1, minWidth: 240 }}>{umpanBalik.msg}</Alert>}
        </Box>
      </Box>

      <Box sx={{ width: { xs: '100%', lg: 420 }, flexShrink: 0, alignSelf: 'stretch' }}>
        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, maxHeight: { lg: 'calc(100vh - 170px)' }, overflowY: { lg: 'auto' }, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Pratinjau tiket {NAMA[jenis]}</Typography>
          <Box sx={{ overflowX: 'auto', pb: 1 }}>
            <StrukPratinjau baris={baris} />
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => cetakBrowser(baris)}>
            Cetak contoh
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Pratinjau memakai pengaturan di layar ini dan belum tersimpan sebelum Anda menekan Simpan. Ukuran huruf di printer asli bisa sedikit berbeda.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
